import { useContext } from 'react';
import classNames from 'classnames';

import { isNumber, isString } from '../../../../utils';
import { Badge } from '../../../Badge/Badge';
import { PageContext } from '../../../../contexts/PageContext';
import { ComponentContext } from '../../../../contexts/ComponentContext';

import { applyPageTaxonomyParams } from './applyPageTaxonomyParams';

import styles from './EndpointParamsList.module.scss';

type Params = Record<string, unknown>;

type Props = {
  params?: Params;
  className?: string;
  small?: boolean;
  inline?: boolean;
};

function EndpointParamsList({
  params = {},
  className,
  small = false,
  inline = false,
}: Props) {
  const { page } = useContext(PageContext);
  const { component } = useContext(ComponentContext);

  const paramsWithTaxonomy = applyPageTaxonomyParams(page, component, params);

  // flatten the params object
  const paramsList = Object.keys(paramsWithTaxonomy).reduce<
    { name: string; value: unknown }[]
  >((accumulator, param) => {
    const value = paramsWithTaxonomy[param];

    if (value === null || value === undefined) {
      return accumulator;
    }

    if (isString(value) || isNumber(value)) {
      accumulator.push({
        name: param,
        value,
      });
    } else {
      Object.keys(value as Record<string, unknown>).forEach((key) => {
        const innerValue = (value as Record<string, unknown>)[key];

        if (innerValue !== null && innerValue !== undefined) {
          accumulator.push({
            name: key,
            value: innerValue,
          });
        }
      });
    }

    return accumulator;
  }, []);

  return (
    <div
      className={classNames(styles.container, className, {
        [styles.isInline]: inline,
      })}
    >
      {paramsList.map(({ name, value }, index) => (
        <Badge variant="grey" key={index} small={small}>
          {name}: {Array.isArray(value) ? value.join(', ') : String(value)}
        </Badge>
      ))}
    </div>
  );
}

export { EndpointParamsList };
